import React, { useState } from "react";
import ReactDOM from "react-dom";
import PropTypes from "prop-types";

import usePopover from "components/common/usePopover";
import { AccountIdentity } from "./MobileAccountMenu";
import AuthenticationService from "service/auth/AuthenticationService";

/**
 * Row 1 — back · name · edit · contact book · overflow menu.
 *
 * Replaces TopBar + the identity strip of PatientHeaderCard, neither of which
 * fits 360px: TopBar's breadcrumbs need two lines for a long name, and the
 * header card's action row carries six controls.
 *
 * This row is also the one thing that stays visible behind an open bottom
 * sheet, so whoever's record is being edited is never off screen. Its height is
 * measured and published as --zym-row1-h; see MobilePatientScreen.
 *
 * NO DIRECT-DIAL BUTTON, per the board (5330:2). Where desktop has a Call
 * button beside the phonebook, here the phonebook is the only call affordance
 * in this row — it lists every number and dials from there. One-tap dial of the
 * primary number is still on the Overview tab's action bar and in the
 * appointment sheet.
 */

const BackIcon = () => (
  <svg viewBox="0 0 16 16" aria-hidden="true" focusable="false">
    <path
      d="M10 2L4 8l6 6"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

/** The app's own pencil — PatientHeaderCard.js:41, the same path verbatim, so
 *  the control that opens the patient-details dialog looks identical on both
 *  screens rather than being a second pencil of my own drawing. */
const EditIcon = () => (
  <svg viewBox="0 0 16 16" fill="currentColor" aria-hidden="true" focusable="false">
    <path d="M3.3332 12.6665H4.1742L10.9985 5.8422L10.1575 5.0012L3.3332 11.8255V12.6665ZM2.3332 13.6665V11.4102L11.1269 2.62037C11.2276 2.52881 11.3389 2.45809 11.4607 2.4082C11.5826 2.3582 11.7104 2.3332 11.844 2.3332C11.9777 2.3332 12.1071 2.35693 12.2324 2.40437C12.3577 2.45181 12.4686 2.52726 12.5652 2.6307L13.3794 3.45504C13.4828 3.55159 13.5565 3.6627 13.6005 3.78837C13.6445 3.91404 13.6665 4.0397 13.6665 4.16537C13.6665 4.29948 13.6436 4.42743 13.5979 4.5492C13.5521 4.67109 13.4793 4.78243 13.3794 4.8832L4.58954 13.6665H2.3332ZM10.5707 5.42904L10.1575 5.0012L10.9985 5.8422L10.5707 5.42904Z" />
  </svg>
);

/** The phonebook/contact-card glyph — PatientHeaderCard.js:55, same geometry. */
const Phonebook = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.6"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
    focusable="false"
  >
    <rect x="6.5" y="3" width="14.5" height="18" rx="2.5" />
    <path d="M3.5 8h3M3.5 12h3M3.5 16h3" />
    <circle cx="13.7" cy="10.2" r="2.3" />
    <path d="M10 16.6a3.9 3.9 0 017.4 0" />
  </svg>
);

const KebabIcon = () => (
  <svg viewBox="0 0 4 16" aria-hidden="true" focusable="false">
    <circle cx="2" cy="3" r="1.5" fill="currentColor" />
    <circle cx="2" cy="8" r="1.5" fill="currentColor" />
    <circle cx="2" cy="13" r="1.5" fill="currentColor" />
  </svg>
);

/* Wide enough for the identity block above Log out — "Doctor ID" and a
   country-coded number on one line each. Matches MobileAccountMenu's. */
const MENU_WIDTH = 248;

const MobileTopBar = ({ title, onBack, onEditPatient, onOpenAddressBook, menu }) => {
  const [open, setOpen] = useState(false);
  const { triggerRef, menuRef, menuStyle } = usePopover(open, () => setOpen(false));

  /* One instance, reused: constructing AuthenticationService re-runs
     setDefaultCookies(), which rewrites the `client` cookie on every render.
     Same reason UserMenu.js holds its own in state. */
  const [auth] = useState(() => new AuthenticationService());

  /* auth.logout() ends in a hard window.location.href = "/", and that reload is
     load-bearing — two module-level singletons only get torn down by a real
     navigation. UserMenu.js documents this at length; do not turn it into a
     router push here either. */
  const logout = () => {
    setOpen(false);
    auth.logout();
  };

  /* usePopover left-aligns to the trigger and has no right-align mode, and this
     trigger is hard against the right edge — so a menu wider than the button
     would be dragged back by the viewport clamp. Right-align it by hand, the
     same correction UserMenu.js makes. */
  const style =
    menuStyle.left != null && triggerRef.current
      ? {
          top: menuStyle.top,
          left: Math.max(8, triggerRef.current.getBoundingClientRect().right - MENU_WIDTH),
        }
      : { top: menuStyle.top, left: menuStyle.left };

  return (
    <div className="zym-topbar">
      <button type="button" className="zym-icon-btn" onClick={onBack} aria-label="Back">
        <BackIcon />
      </button>

      {/* Name and pencil are one control, as on desktop, where the whole name
          row opens the dialog. Kept as a single button so the target is the
          text rather than a 16px glyph — and a plain heading where there is
          nothing to edit, rather than a button that does nothing. */}
      {onEditPatient ? (
        <button
          type="button"
          className="zym-topbar-name"
          onClick={onEditPatient}
          title="View and edit patient details"
        >
          <span className="zym-topbar-title">{title}</span>
          <span className="zym-topbar-pencil" aria-hidden="true">
            <EditIcon />
          </span>
        </button>
      ) : (
        <h1 className="zym-topbar-name zym-topbar-title">{title}</h1>
      )}

      {onOpenAddressBook && (
        <button
          type="button"
          className="zym-icon-btn is-accent"
          onClick={onOpenAddressBook}
          /* Named as the dialog it opens is titled, which is what desktop's
             header now says too (upstream, 2026-09-17). It used to say "Call
             details" here and "Address book" there — three names, one dialog. */
          aria-label="Phonebook"
          title="Phonebook"
        >
          <Phonebook />
        </button>
      )}

      {/* Per usage, because the two screens that wear this bar differ: the
          patient screen is reached from a list whose avatar already offers
          Log out, while Appointment details has no avatar anywhere on it and
          would otherwise strand you on a screen you cannot sign out of. */}
      {menu !== false && (
      <button
        type="button"
        ref={triggerRef}
        className="zym-icon-btn"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label="More"
        onClick={() => setOpen((o) => !o)}
      >
        <KebabIcon />
      </button>
      )}

      {menu !== false && open &&
        ReactDOM.createPortal(
          <div
            ref={menuRef}
            className="zym-menu"
            style={style}
            role="menu"
            aria-label="Account"
          >
            {/* The same identity block the appointments screen's avatar opens.
                Two account menus that disagreed about what an account is would
                be worse than either. */}
            <AccountIdentity />
            <button
              type="button"
              className="zym-menu-item is-danger"
              role="menuitem"
              onClick={logout}
            >
              Log out
            </button>
          </div>,
          document.body
        )}
    </div>
  );
};

MobileTopBar.propTypes = {
  /** False hides the ⋮ and its menu. Defaults to showing them. */
  menu: PropTypes.bool,
  title: PropTypes.string,
  onBack: PropTypes.func.isRequired,
  /** Opens PatientDetailsDialog — owned by the screen, not this row. */
  onEditPatient: PropTypes.func,
  /** useCallFlow's openAddressBook. Its UI is mounted by the screen. */
  onOpenAddressBook: PropTypes.func,
};

export default MobileTopBar;
